import Plyr from 'plyr';
import { freezeStickyHeaders, restoreStickyHeaders } from './components/drawer';

const OPEN_CLASS = 'is-lvz-plyr-modal-open';
const HIDDEN_CLASS = 'lvz-plyr-modal--hidden';

const PLYR_OPTIONS = {
	autoplay: true,
	controls: ['play', 'progress', 'current-time', 'mute', 'volume', 'captions', 'settings', 'pip', 'airplay', 'fullscreen'],
	youtube: { noCookie: false, rel: 0, showinfo: 0, iv_load_policy: 3, modestbranding: 1 },
	vimeo: { byline: false, portrait: false, title: false }
};

let modalEl = null;
let playerWrapEl = null;
let dialogEl = null;
let plyrInstance = null;
let lastTriggerEl = null;
// True when this modal froze the sticky headers (not already frozen by an open drawer / side panel).
let headersFrozenByModal = false;

export function parseVideoUrl(url) {
	if (!url) {
		return null;
	}

	const text = String(url).trim();
	const youtube = text.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);

	if (youtube) {
		return { type: 'youtube', id: youtube[1] };
	}

	const vimeo = text.match(/vimeo\.com\/(?:channels\/[^/]+\/|groups\/[^/]+\/videos\/|video\/)?(\d+)/);
	if (vimeo) {
		return { type: 'vimeo', id: vimeo[1] };
	}

	return { type: 'html5', src: text };
}

function createEmbed(src) {
	const el = document.createElement('div');
	el.className = 'plyr__video-embed lvz-plyr-modal__player';
	el.innerHTML = '<iframe src="' + src + '" allowfullscreen allowtransparency allow="autoplay"></iframe>';
	return el;
}

export function createPlayerElement(parsed) {
	if (parsed.type === 'youtube') {
		const origin = encodeURIComponent(window.location.origin);
		return createEmbed(
			'https://www.youtube.com/embed/' +
				parsed.id +
				'?origin=' +
				origin +
				'&iv_load_policy=3&modestbranding=1&playsinline=1&rel=0&enablejsapi=1'
		);
	}

	if (parsed.type === 'vimeo') {
		return createEmbed(
			'https://player.vimeo.com/video/' +
				parsed.id +
				'?loop=false&byline=false&portrait=false&title=false&speed=true&transparent=0&gesture=media'
		);
	}

	const video = document.createElement('video');
	video.className = 'lvz-plyr-modal__player';
	video.setAttribute('playsinline', '');
	video.setAttribute('preload', 'metadata');
	video.src = parsed.src;
	return video;
}

function getModal() {
	if (modalEl) {
		return modalEl;
	}

	modalEl = document.querySelector('[data-lvz-plyr-modal="true"]');

	if (!modalEl) {
		modalEl = document.createElement('div');
		modalEl.className = 'lvz-plyr-modal ' + HIDDEN_CLASS;
		modalEl.setAttribute('data-lvz-plyr-modal', 'true');
		modalEl.setAttribute('hidden', '');
		modalEl.setAttribute('aria-hidden', 'true');
		modalEl.innerHTML =
			'<div class="lvz-plyr-modal__backdrop" data-lvz-plyr-modal-close="true" aria-hidden="true"></div>' +
			'<div class="lvz-plyr-modal__dialog" role="dialog" aria-modal="true" tabindex="-1">' +
			'<div class="lvz-plyr-modal__content">' +
			'<button type="button" class="lvz-plyr-modal__close" data-lvz-plyr-modal-close="true" aria-label="Close">' +
			'<i class="icon-close text-3xl-2 leading-none" aria-hidden="true"></i>' +
			'</button>' +
			'<div class="lvz-plyr-modal__player-wrap" data-lvz-plyr-modal-player="true"></div>' +
			'</div></div>';
		document.body.appendChild(modalEl);
	}

	playerWrapEl = modalEl.querySelector('[data-lvz-plyr-modal-player="true"]');
	dialogEl = modalEl.querySelector('.lvz-plyr-modal__dialog');

	if (modalEl.getAttribute('data-lvz-plyr-modal-bound') !== 'true') {
		modalEl.querySelectorAll('[data-lvz-plyr-modal-close="true"]').forEach(function (btn) {
			btn.addEventListener('click', function (e) {
				e.preventDefault();
				closeLvzPlyrModal();
			});
		});
		modalEl.setAttribute('data-lvz-plyr-modal-bound', 'true');
	}

	return modalEl;
}

function clearPlayer() {
	if (plyrInstance) {
		plyrInstance.destroy();
		plyrInstance = null;
	}
	if (playerWrapEl) {
		playerWrapEl.innerHTML = '';
	}
}

function showModal(show) {
	if (!modalEl) {
		return;
	}
	if (show) {
		modalEl.removeAttribute('hidden');
		modalEl.setAttribute('aria-hidden', 'false');
		modalEl.classList.remove(HIDDEN_CLASS);
	} else {
		modalEl.setAttribute('hidden', '');
		modalEl.setAttribute('aria-hidden', 'true');
		modalEl.classList.add(HIDDEN_CLASS);
	}
}

function onEscapeKey(e) {
	if (e.key === 'Escape') {
		e.preventDefault();
		closeLvzPlyrModal();
	}
}

export function closeLvzPlyrModal() {
	if (!modalEl || modalEl.classList.contains(HIDDEN_CLASS)) {
		return;
	}

	document.removeEventListener('keydown', onEscapeKey, true);
	clearPlayer();
	showModal(false);
	document.documentElement.classList.remove(OPEN_CLASS);

	if (headersFrozenByModal) {
		restoreStickyHeaders();
		headersFrozenByModal = false;
	}

	if (lastTriggerEl) {
		try {
			lastTriggerEl.focus();
		} catch (err) {
			// Focus can fail if the element was removed meanwhile: nothing to restore.
		}
	}
	lastTriggerEl = null;
}

export function openLvzPlyrModal(url, title, trigger, plyrOptions) {
	const parsed = parseVideoUrl(url);
	if (!parsed) {
		return;
	}

	getModal();
	clearPlayer();

	lastTriggerEl = trigger || null;
	const mediaEl = createPlayerElement(parsed);
	playerWrapEl.appendChild(mediaEl);

	if (title && dialogEl) {
		dialogEl.setAttribute('aria-label', title);
	}

	// Same fix as drawers / side panels: pin the sticky headers where they are before the scroll lock,
	// otherwise they disappear behind the modal (AUR-4899).
	if (!document.querySelector('.drawer-frozen-header-placeholder')) {
		freezeStickyHeaders();
		headersFrozenByModal = true;
	}

	showModal(true);
	document.documentElement.classList.add(OPEN_CLASS);
	document.addEventListener('keydown', onEscapeKey, true);

	plyrInstance = new Plyr(mediaEl, Object.assign({}, PLYR_OPTIONS, plyrOptions || {}));

	let hasStarted = false;
	let hasEnded = false;
	// Tracking video_title: the title of the video shown in the player (YouTube / Vimeo metadata),
	// falling back to the title configured on the component.
	let trackingTitle = title;

	plyrInstance.on('ready', function () {
		const embed = plyrInstance && plyrInstance.embed;

		try {
			if (embed && typeof embed.getVideoData === 'function') {
				trackingTitle = (embed.getVideoData() || {}).title || trackingTitle;
			} else if (embed && typeof embed.getVideoTitle === 'function') {
				embed.getVideoTitle().then(function (vimeoTitle) {
					trackingTitle = vimeoTitle || trackingTitle;
				});
			}
		} catch (err) {
			// keep the component title
		}
	});

	function getVideoTimePercent() {
		if (!plyrInstance || !plyrInstance.duration) {
			return '';
		}

		let percent = Math.round((plyrInstance.currentTime / plyrInstance.duration) * 100);

		if (percent < 0) {
			percent = 0;
		}

		if (percent > 100) {
			percent = 100;
		}

		return String(percent) + '%';
	}

	function buildTrackingDetail() {
		return {
			title: trackingTitle,
			videoTime: getVideoTimePercent()
		};
	}

	plyrInstance.on('playing', function () {
		if (hasEnded) {
			return;
		}

		const detail = buildTrackingDetail();

		if (!hasStarted) {
			hasStarted = true;
			if (window.lavazzaGtm && window.lavazzaGtm.media) {
				window.lavazzaGtm.media.trackVideoStart(detail);
			}
			return;
		}

		if (window.lavazzaGtm && window.lavazzaGtm.media) {
			window.lavazzaGtm.media.trackVideoPlay(detail);
		}
	});

	plyrInstance.on('pause', function () {
		if (hasEnded) {
			return;
		}

		if (window.lavazzaGtm && window.lavazzaGtm.media) {
			window.lavazzaGtm.media.trackVideoPause(buildTrackingDetail());
		}
	});

	plyrInstance.on('ended', function () {
		hasEnded = true;
		if (window.lavazzaGtm && window.lavazzaGtm.media) {
			window.lavazzaGtm.media.trackVideoEnded(buildTrackingDetail());
		}
	});

	requestAnimationFrame(function () {
		if (dialogEl) {
			try {
				dialogEl.focus();
			} catch (err) {
				// Focus can fail if the dialog was removed meanwhile: nothing to do.
			}
		}
	});
}

export function initLvzPlyrDelegation(options) {
	if (typeof window !== 'undefined' && window.__lvzPlyrModalDelegation) {
		return;
	}

	const triggerSel = (options && options.triggerSelector) || '[data-lvz-plyr-trigger="true"]';
	const sourceSel = '[data-video-url]';
	const urlAttr = (options && options.urlAttribute) || 'data-video-url';
	const titleAttr = (options && options.titleAttribute) || 'data-video-title';
	const scope = (options && options.scope) || document;
	const extraPlyrOptions = options && options.plyrOptions;

	scope.addEventListener(
		'click',
		function (e) {
			const target = e.target;
			if (!target || !target.closest) {
				return;
			}

			const trigger = target.closest(triggerSel);
			if (!trigger) {
				return;
			}
			if (scope !== document && !scope.contains(trigger)) {
				return;
			}

			const card = trigger.closest(sourceSel);
			const url = card && card.getAttribute(urlAttr);
			if (!url) {
				return;
			}

			e.preventDefault();
			openLvzPlyrModal(url, card.getAttribute(titleAttr) || '', trigger, extraPlyrOptions);
		},
		true
	);

	if (typeof window !== 'undefined') {
		window.__lvzPlyrModalDelegation = true;
	}
}
